import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ActivateOnboardingCuePayload, DismissOnboardingCuePayload, OnboardingCueState } from "./index.types";

const initialState: OnboardingCueState = {
  activeCues: [],
  dismissedCues: [],
};

export const onboardingCueSlice = createSlice({
  name: "onboardingCue",
  initialState,
  reducers: {
    // Mark a one-time onboarding cue as showing for the rest of this session. Held here
    // rather than in the consuming component's state because that component remounts on a
    // patient switch, a dashboard tab change, or a lazy chunk re-resolving - and by then the
    // server has already been told the cue was shown, so a remount would hide it. This slice
    // is deliberately left out of the persist whitelist, so a reload or logout ends the cue.
    activateOnboardingCue: (state, { payload }: PayloadAction<ActivateOnboardingCuePayload>) => {
      // A cue the user has already acted on never comes back for the rest of the session
      if (state.dismissedCues.includes(payload.cueKey)) {
        return;
      }
      if (!state.activeCues.includes(payload.cueKey)) {
        state.activeCues.push(payload.cueKey);
      }
    },

    // Retire a cue because the user has used the control it points at. Keyed on the cue and
    // not on the item acted upon: the cue teaches what the control does, so pressing it on
    // any item has taught it, and which row was pressed must not change the outcome.
    dismissOnboardingCue: (state, { payload }: PayloadAction<DismissOnboardingCuePayload>) => {
      if (!state.dismissedCues.includes(payload.cueKey)) {
        state.dismissedCues.push(payload.cueKey);
      }
      state.activeCues = state.activeCues.filter((cueKey) => cueKey !== payload.cueKey);
    },
  },
});

export const { activateOnboardingCue, dismissOnboardingCue } = onboardingCueSlice.actions;

export default onboardingCueSlice.reducer;
